import React from 'react';
import { Link } from 'react-router-dom';
import {
  LICENCJA_PO_SLUGU,
  LICENCJE,
  PROG_PROCENT,
  arkuszPrzedmiotu,
  przedmiotyLicencji,
  pytanWEgzaminie,
} from '../data/licencje';
import JsonLd from '../seo/JsonLd';
import { breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import Faq from './Faq';
import styles from '../css/modules/Nauka.module.css';

/**
 * Strona filarowa jednego uprawnienia. Jeden komponent obsługuje wszystkie -
 * treść bierze z src/data/licencje.js, więc dodanie kolejnej licencji nie
 * wymaga nowego pliku.
 */

const faqLicencji = licencja => {
  const przedmioty = przedmiotyLicencji(licencja);
  const pozycje = [];

  if (licencja.tryb === 'przedmioty') {
    pozycje.push({
      question: `Ile testów obejmuje egzamin teoretyczny na ${licencja.nazwa}?`,
      answer: `Egzamin teoretyczny to ${przedmioty.length} osobnych testów, po jednym z każdego przedmiotu, zdawanych przed Lotniczą Komisją Egzaminacyjną ULC. Każdy zaliczasz niezależnie, przy ${PROG_PROCENT} procentach poprawnych odpowiedzi. Łącznie odpowiadasz na ${pytanWEgzaminie(licencja)} pytań.`,
    });
    pozycje.push({
      question: 'Ile mam podejść i ile czasu na zaliczenie całości?',
      answer:
        'Do jednego przedmiotu można podejść maksymalnie cztery razy, a wszystkie przedmioty trzeba zaliczyć w nie więcej niż sześciu sesjach egzaminacyjnych i w ciągu 18 miesięcy. Komplet zdanej teorii jest ważny 24 miesiące.',
    });
  } else {
    pozycje.push({
      question: `Jak wygląda egzamin na ${licencja.dopelniacz}?`,
      answer: `${licencja.egzaminOpis} Test liczy ${licencja.arkusz.pytan} pytań, a na jego rozwiązanie przewidziano ${licencja.arkusz.minut} minut.`,
    });
  }

  pozycje.push({
    question: `Od ilu lat można ubiegać się o ${licencja.dopelniacz}?`,
    answer: licencja.wiekSamodzielny
      ? `Wymagany wiek to ${licencja.wiek} lat. Do lotu samodzielnego w trakcie szkolenia wystarczy ukończone ${licencja.wiekSamodzielny} lat, ale sam dokument zostanie wydany dopiero po osiągnięciu pełnego wymaganego wieku.`
      : `Wymagany wiek to ${licencja.wiek} lat.`,
  });

  pozycje.push({
    question: `Do czego uprawnia ${licencja.nazwa}?`,
    answer: licencja.uprawnia,
  });

  pozycje.push({
    question: `Kto prowadzi egzamin na ${licencja.dopelniacz}?`,
    answer: `Egzamin przeprowadza ${licencja.organ}. Nasz serwis przygotowuje do części teoretycznej i nie jest powiązany z organem egzaminującym.`,
  });

  return pozycje;
};

const LicencjaPage = ({ slug }) => {
  const licencja = LICENCJA_PO_SLUGU[slug];
  if (!licencja) return null;

  const faq = faqLicencji(licencja);
  const przedmioty = przedmiotyLicencji(licencja);
  const pozostale = LICENCJE.filter(l => l.slug !== slug);
  const naglowek =
    licencja.rodzina === 'bsp' ? `Testy na uprawnienia ${licencja.krotko}` : `Testy na ${licencja.nazwa}`;

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: licencja.nazwa, path: licencja.path },
      ])} />
      <JsonLd data={faqJsonLd(faq)} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / {licencja.nazwa}
      </nav>

      <h1 className={styles.h1}>{naglowek}</h1>
      <p className={styles.lead}>
        Autorskie pytania napisane pod program egzaminu teoretycznego, każde z wyjaśnieniem
        odpowiedzi i wskazaniem przepisu. Ucz się przedmiotami albo sprawdź się w pojedynku,
        w którym liczy się nie tylko wiedza, ale i czas na odpowiedź.
      </p>

      <div className={styles.akcje}>
        <Link className={styles.cta} to="/intro">Rozegraj darmowy test</Link>
        <Link className={styles.ctaDrugi} to={`/nauka/${licencja.slug}`}>Ucz się przedmiotami</Link>
      </div>

      <div className={styles.fakty}>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Egzamin teoretyczny</p>
          <p className={styles.faktWartosc}>
            {licencja.tryb === 'przedmioty'
              ? `${przedmioty.length} testów / ${pytanWEgzaminie(licencja)} pytań`
              : `${licencja.arkusz.pytan} pytań / ${licencja.arkusz.minut} min`}
          </p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Próg zaliczenia</p>
          <p className={styles.faktWartosc}>{PROG_PROCENT} procent</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Minimalny wiek</p>
          <p className={styles.faktWartosc}>{licencja.wiek} lat</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Uprawnienie</p>
          <p className={styles.faktWartosc}>{licencja.krotko}</p>
        </div>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Do czego uprawnia {licencja.nazwa}</h2>
        <p>{licencja.uprawnia}</p>
        <p>{licencja.egzaminOpis}</p>
        <p>
          Egzamin przeprowadza {licencja.organ}. Poza teorią trzeba zaliczyć część praktyczną -
          nasz serwis przygotowuje wyłącznie do teorii.
        </p>

        <h2 className={styles.h2}>Zakres materiału</h2>
        <p>
          {licencja.tryb === 'przedmioty'
            ? 'Każdy przedmiot zdaje się osobno, a przy każdym podajemy liczbę pytań i limit czasu obowiązujące na egzaminie. Wartości pochodzą z tabeli publikowanej przez Lotniczą Komisję Egzaminacyjną - przed sesją warto sprawdzić aktualny komunikat ULC.'
            : 'Pytania obejmują wszystkie zagadnienia programu i trafiają do jednego testu. Poniżej rozpiska tematów wraz z liczbą pytań w naszej bazie.'}
        </p>
      </div>

      <div className={styles.kafle}>
        {przedmioty.map(p => {
          const arkusz = arkuszPrzedmiotu(licencja, p.slug);
          return (
            <Link key={p.slug} to={`/nauka/${licencja.slug}/${p.slug}`} className={styles.kafel}>
              {licencja.tryb === 'przedmioty' && arkusz && (
                <span className={styles.kafelUdzial}>
                  {arkusz.pytan} pytań / {arkusz.minut} min
                </span>
              )}
              <span className={styles.kafelNazwa}>{p.nazwa}</span>
              <span className={styles.kafelOpis}>{p.opis}</span>
            </Link>
          );
        })}
      </div>

      <h2 className={styles.h2}>Najczęstsze pytania</h2>
      <Faq items={faq} />

      <div className={styles.sasiedzi}>
        <Link to="/egzamin-teoretyczny-ulc" className={styles.sasiad}>
          Jak wygląda egzamin teoretyczny w ULC
        </Link>
        {pozostale.map(l => (
          <Link key={l.slug} to={l.path} className={styles.sasiad}>
            {l.rodzina === 'bsp' ? `Egzamin na drona ${l.krotko}` : `Testy na ${l.nazwa}`}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default LicencjaPage;
